// src/context/AuthContext.tsx

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import type { Database } from '../types/database';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: Profile | null;
  supabaseUser: SupabaseUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isConfigured: boolean;
}

interface RegisterData {
  email: string;
  name: string;
  role: 'athlete' | 'coach' | 'club' | 'federation' | 'journalist' | 'fan';
  sport?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Check if Supabase is properly configured
const isSupabaseConfigured = () => {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  
  return supabaseUrl && 
         supabaseKey && 
         !supabaseUrl.includes('placeholder') && 
         !supabaseKey.includes('placeholder') &&
         supabaseUrl !== 'your_supabase_project_url' && 
         supabaseKey !== 'your_supabase_anon_key' &&
         supabaseUrl.startsWith('https://');
};

// Generate demo user data
const createDemoUser = (email: string, userData?: RegisterData): Profile => {
  const demoId = 'demo-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  
  return {
    id: demoId,
    email: email.toLowerCase(),
    name: userData?.name || 'Usuario Demo',
    role: userData?.role || 'athlete',
    sport: userData?.sport || 'Basketball',
    avatar_url: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    bio: 'Perfil de demostración para FemSportNet',
    location: 'Madrid, España',
    date_of_birth: null,
    verified: true,
    followers_count: Math.floor(Math.random() * 500) + 50,
    following_count: Math.floor(Math.random() * 200) + 20,
    posts_count: Math.floor(Math.random() * 50) + 5,
    achievements_count: Math.floor(Math.random() * 15) + 3,
    social_links: {
      instagram: 'https://instagram.com/demo_athlete',
      twitter: 'https://twitter.com/demo_athlete'
    },
    privacy_settings: {
      profile_visibility: 'public',
      stats_visibility: 'public',
      media_visibility: 'public'
    },
    notification_settings: {
      new_followers: true,
      messages: true,
      achievements: true
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  };
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<Profile | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const isConfigured = isSupabaseConfigured();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if (!isConfigured) {
          console.log('🔧 Modo demo activado - Supabase no configurado');
          setLoading(false);
          return;
        }

        console.log('🔄 Inicializando autenticación...');
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ Error obteniendo sesión:', error);
          setLoading(false);
          return;
        }

        console.log('📋 Sesión:', session?.user?.email || 'Sin sesión activa');

        setSupabaseUser(session?.user ?? null);
        if (session?.user) {
          await fetchProfile(session.user.id);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ Error inicializando auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔄 Cambio de estado auth:', event, session?.user?.email || 'Sin sesión');
      setSupabaseUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isConfigured]);

  const fetchProfile = async (userId: string) => {
    try {
      if (!isConfigured) {
        setUser(null);
        setLoading(false);
        return;
      }

      console.log('👤 Obteniendo perfil para:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('❌ Error obteniendo perfil:', error);
        setUser(null);
      } else {
        console.log('✅ Perfil obtenido:', data?.name);
        setUser(data);
      }
    } catch (error) {
      console.error('❌ Error obteniendo perfil:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      console.log('🔐 Intentando login para:', email);
      
      // Validar entrada
      if (!email?.trim() || !password) {
        throw new Error('Email y contraseña son requeridos');
      }

      if (!email.includes('@')) {
        throw new Error('Por favor ingresa un email válido');
      }

      if (password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }

      // Modo demo si Supabase no está configurado
      if (!isConfigured) {
        console.log('🎭 Modo demo: simulando login exitoso');
        const demoUser = createDemoUser(email);
        setUser(demoUser);
        setSupabaseUser({
          id: demoUser.id,
          email: email,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated'
        } as SupabaseUser);
        return;
      }

      // Verificar conexión a Supabase antes de intentar login
      try {
        const { data: testData, error: testError } = await supabase
          .from('profiles')
          .select('count')
          .limit(1);
        
        if (testError) {
          console.error('❌ Error de conexión a Supabase:', testError);
          throw new Error('Error de conexión a la base de datos. Verifica tu configuración de Supabase.');
        }
      } catch (connectionError) {
        console.error('❌ No se puede conectar a Supabase:', connectionError);
        throw new Error('No se puede conectar a la base de datos. Verifica que Supabase esté configurado correctamente.');
      }

      // Login real con Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password: password,
      });

      if (error) {
        console.error('❌ Error de login:', error);
        
        // Manejar errores específicos de Supabase
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Email o contraseña incorrectos. Verifica tus credenciales.');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Por favor confirma tu email antes de iniciar sesión.');
        } else if (error.message.includes('Too many requests')) {
          throw new Error('Demasiados intentos de login. Espera unos minutos e intenta nuevamente.');
        } else if (error.message.includes('User not found')) {
          throw new Error('No existe una cuenta con este email. ¿Quieres registrarte?');
        } else {
          throw new Error(`Error de login: ${error.message}`);
        }
      }

      if (!data.user) {
        throw new Error('Error al iniciar sesión');
      }

      console.log('✅ Login exitoso para:', data.user.email);
    } catch (error) {
      console.error('❌ Error en login:', error);
      
      // Manejar errores de red específicamente
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Error de conexión. Verifica tu conexión a internet y que Supabase esté configurado correctamente.');
      }
      
      throw error;
    }
  };

  const register = async (userData: RegisterData, password: string) => {
    try {
      console.log('📝 Intentando registro para:', userData.email);
      
      // Validar datos de entrada
      if (!userData.email?.trim() || !userData.name?.trim() || !password) {
        throw new Error('Todos los campos requeridos deben ser completados');
      }

      if (!userData.email.includes('@')) {
        throw new Error('Por favor ingresa un email válido');
      }

      if (password.length < 6) {
        throw new Error('La contraseña debe tener al menos 6 caracteres');
      }

      if (userData.name.trim().length < 2) {
        throw new Error('El nombre debe tener al menos 2 caracteres');
      }

      if ((userData.role === 'athlete' || userData.role === 'coach') && !userData.sport?.trim()) {
        throw new Error('El deporte es requerido para atletas y entrenadores');
      }

      // Modo demo si Supabase no está configurado
      if (!isConfigured) {
        console.log('🎭 Modo demo: simulando registro exitoso');
        const demoUser = createDemoUser(userData.email, userData);
        setUser(demoUser);
        setSupabaseUser({
          id: demoUser.id,
          email: userData.email,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          app_metadata: {},
          user_metadata: {},
          aud: 'authenticated'
        } as SupabaseUser);
        return;
      }
      
      // Registro real con Supabase
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email.trim().toLowerCase(),
        password: password,
        options: {
          emailRedirectTo: undefined,
          data: {
            name: userData.name.trim(),
            role: userData.role,
            sport: userData.sport?.trim()
          }
        }
      });

      if (authError) {
        console.error('❌ Error de registro auth:', authError);
        throw authError;
      }

      if (!authData.user) {
        throw new Error('Error al crear el usuario');
      }

      console.log('✅ Usuario auth creado:', authData.user.email);

      // Crear perfil en la base de datos
      const profileData = {
        id: authData.user.id,
        email: userData.email.trim().toLowerCase(),
        name: userData.name.trim(),
        role: userData.role,
        sport: userData.sport?.trim() || null,
        avatar_url: 'https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
        verified: false,
        social_links: {
          instagram: '',
          twitter: '',
          facebook: '',
          website: ''
        },
        privacy_settings: {
          profile_visibility: 'public',
          stats_visibility: 'public',
          media_visibility: 'public'
        },
        notification_settings: {
          new_followers: true,
          messages: true,
          achievements: true
        }
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .insert(profileData);

      if (profileError) {
        console.error('❌ Error creando perfil:', profileError);
        throw new Error('Error al crear el perfil');
      }

      console.log('✅ Perfil creado exitosamente');

      // Si no hay sesión automática, intentar login
      if (!authData.session) {
        console.log('🔄 Sin sesión automática, intentando login...');
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: userData.email.trim().toLowerCase(),
          password: password,
        });
        
        if (signInError) {
          console.error('❌ Error en auto-login:', signInError);
          throw new Error('Cuenta creada. Por favor inicia sesión manualmente.');
        }
      }

    } catch (error) {
      console.error('❌ Error en registro:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) throw new Error('No user logged in');

    if (!isConfigured) {
      // Modo demo - actualizar estado local
      setUser({ ...user, ...updates });
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setUser({ ...user, ...updates });
    } catch (error) {
      console.error('❌ Error actualizando perfil:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('🚪 Cerrando sesión...');
      
      if (!isConfigured) {
        // Modo demo - limpiar estado
        setUser(null);
        setSupabaseUser(null);
        console.log('✅ Logout demo exitoso');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Error en logout:', error);
        throw error;
      }
      console.log('✅ Logout exitoso');
    } catch (error) {
      console.error('❌ Error en logout:', error);
      throw error;
    }
  };

  const value = {
    user,
    supabaseUser,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isConfigured,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
